import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowUpRight, ArrowDownRight } from "lucide-react";

export default function NewTransactionDialog({ open, onOpenChange, accounts, onSaved }) {
  const [type, setType] = useState("gelir");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [accountId, setAccountId] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setType("gelir");
    setAmount("");
    setDescription("");
    setDate(new Date().toISOString().slice(0, 10));
    setAccountId("");
  };

  const submit = async () => {
    if (!accountId) return toast.error("Lütfen bir hesap seçin");
    if (!amount || Number(amount) <= 0) return toast.error("Geçerli bir tutar girin");
    setSaving(true);
    try {
      await api.post("/transactions", {
        type,
        amount: Number(amount),
        description,
        date,
        account_id: accountId,
      });
      toast.success(type === "gelir" ? "Gelir eklendi" : "Gider eklendi");
      reset();
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bir hata oluştu");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="new-transaction-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Yeni İşlem</DialogTitle>
          <DialogDescription>Gelir veya gider kaydı ekleyin.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              data-testid="type-gelir-btn"
              onClick={() => setType("gelir")}
              className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors ${
                type === "gelir"
                  ? "bg-emerald-50 border-emerald-300 text-emerald-700"
                  : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
            >
              <ArrowUpRight className="h-4 w-4" /> Gelir
            </button>
            <button
              type="button"
              data-testid="type-gider-btn"
              onClick={() => setType("gider")}
              className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors ${
                type === "gider"
                  ? "bg-rose-50 border-rose-300 text-rose-700"
                  : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
            >
              <ArrowDownRight className="h-4 w-4" /> Gider
            </button>
          </div>

          <div className="space-y-1.5">
            <Label>Hesap</Label>
            <Select value={accountId} onValueChange={setAccountId}>
              <SelectTrigger data-testid="tx-account-select">
                <SelectValue placeholder="Hesap seçin" />
              </SelectTrigger>
              <SelectContent>
                {accounts.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.name} {a.bank_name ? `· ${a.bank_name}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Tutar (₺)</Label>
              <Input
                data-testid="tx-amount-input"
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0,00"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Tarih</Label>
              <Input
                data-testid="tx-date-input"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Açıklama</Label>
            <Textarea
              data-testid="tx-description-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Örn. Satış geliri, kira ödemesi..."
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            data-testid="save-transaction-btn"
            onClick={submit}
            disabled={saving}
            className="bg-indigo-600 hover:bg-indigo-700 text-white w-full sm:w-auto"
          >
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
